import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getCommonConfig } from "@/services/commonconfig";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

export type CommonConfig = {
  encryptionKey: string;
  LogoutTime: number;
  WarningTime: number;
  InactiveDays: number;

  TourIcon: boolean;
  NotificationIcon: boolean;
  LanguageSwitchIcon: boolean;

  HighContrast: boolean;
  UnderlineLinks: boolean;
  TextSpacing: boolean;

  ReduceMotion: boolean;

  KeyboardNavigation: boolean;
  FocusIndicators: boolean;

  CursorSize: boolean;

  TTS: boolean;
  SpeechRate: boolean;
  TTSOnFocus: boolean;
  TTSOnHover: boolean;

  ScreenReaderOptimization: boolean;
  DyslexiaFriendlyFont: boolean;

  LightTheme: boolean;
  DarkTheme: boolean;
  VibrantTheme: boolean;

  AllProgramsVisibility: string;
  FluxxURL: string;
  PowerBIURL: string;

  translationDbEnabled: boolean;
};

type ConfigState = {
  commonConfig: CommonConfig | null;
  status: RequestStatus;
  error: string | null;
};

const initialState: ConfigState = {
  commonConfig: null,
  status: "idle",
  error: null,
};



export const fetchCommonConfig = createAsyncThunk<CommonConfig | null, void>(
  "config/fetchCommonConfig",
  async () => {
    const res = await getCommonConfig();

    return res;
  }
);

let translationDbEnabled = false;

export const setTranslationDbEnabled = (value: boolean) => {
  translationDbEnabled = value;
};

export const isTranslationDbEnabled = () => {
  return translationDbEnabled;
};

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    resetConfigState(state) {
      state.commonConfig = null;
      state.status = "idle";
      state.error = null;
      setTranslationDbEnabled(false);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommonConfig.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })

      .addCase(fetchCommonConfig.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.commonConfig = action.payload;
          setTranslationDbEnabled(
    action.payload?.translationDbEnabled ?? false
  );
      })

      .addCase(fetchCommonConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load common config";
      });
  },
});

export const { resetConfigState } = configSlice.actions;

export const selectCommonConfig = (s: any): CommonConfig | null => s.config.commonConfig;

// Individual flags

export const selectTourIcon = (s: any): boolean => s.config.commonConfig?.TourIcon ?? false;

export const selectNotificationIcon = (s: any): boolean =>
  s.config.commonConfig?.NotificationIcon ?? false;

export const selectLanguageSwitchIcon = (s: any): boolean =>
  s.config.commonConfig?.LanguageSwitchIcon ?? false;

export const selectLogoutTime = (s: any): number => s.config.commonConfig?.LogoutTime ?? 2;

export const selectWarningTime = (s: any): number => s.config.commonConfig?.WarningTime ?? 1;

export const selectInactiveDays = (s: any): number => s.config.commonConfig?.InactiveDays ?? 100;

export const selectFluxxURL = (s: any): string => s.config.commonConfig?.FluxxURL ?? "";

export const selectPowerBIURL = (s: any): string => s.config.commonConfig?.PowerBIURL ?? "";

export const selectNotificationUrl1 = (s: any): string =>
  s.config.commonConfig?.NotificationFluxx1 ?? "";

export const selectNotificationUrl2 = (s: any): string =>
  s.config.commonConfig?.NotificationFluxx2 ?? "";

export const selectTaskPrioritiesUrl1 = (s: any): string =>
  s.config.commonConfig?.TaskPrioritiesFluxx1 ?? "";

export const selectTaskPrioritiesUrl2 = (s: any): string =>
  s.config.commonConfig?.TaskPrioritiesFluxx2 ?? "";

export const selectPowerBIOversightUrl = (s: any): string =>
  s.config.commonConfig?.PowerBIOversightUrl ?? "";

export const selectPowerBIFinancialsUrl = (s: any): string =>
  s.config.commonConfig?.PowerBIFinancialsUrl ?? "";

export const selectPowerBIAnalyticsUrl = (s: any): string =>
  s.config.commonConfig?.PowerBIAnalyticsUrl ?? "";

export const selectPowerBIIdeaTrackerUrlUrl = (s: any): string =>
  s.config.commonConfig?.PowerBIIdeaTrackerUrl ?? "";

export const selectPowerBIProgramImpactUrl = (s: any): string =>
  s.config.commonConfig?.PowerBIProgramImpactUrl ?? "";

export const selectDisbursementFluxxUrl1 = (s: any): string =>
  s.config.commonConfig?.DisbursementFluxx1 ?? "";

export const selectDisbursementFluxxUrl2 = (s: any): string =>
  s.config.commonConfig?.DisbursementFluxx2 ?? "";

export const selectPartnerDirectoryFluxxUrl1 = (s: any): string =>
  s.config.commonConfig?.PartnerDirectoryFluxx1 ?? "";

export const selectPartnerDirectoryFluxxUrl2 = (s: any): string =>
  s.config.commonConfig?.PartnerDirectoryFluxx2 ?? "";

export const selectOutlookMeetingsUrl = (s: any): string =>
  s.config.commonConfig?.OutlookMeetingsUrl ?? "";

export const selectPowerBIIdeaTrackerUrl = (s: any): string =>
  s.config.commonConfig?.PowerBIIdeaTrackerUrl ?? "";

export default configSlice.reducer;
